import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { CircleOff } from "lucide-react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { alertNotification } from "../../actions/alertNotification";
import { updateCurrUser } from "../../actions/auth";
import { setContentManagerDataDetails, setContentManagerDataView, setFileMetaData, setSelectedContentItem } from "../../actions/contentManager";
import { setRedirectedFromSettings } from "../../actions/projects";
import { updateBookmark } from "../../components/Bookmarks/bookmarks.service";
import { button, icon, label, notifyIcon, number, quote, route, tooltip } from "../../config";
import { changeUserNotifEmailControl } from "../../shared/services/user.service";
import { createDynamicTeamName } from "../../utils/common";
import { getNotification } from "../../utils/common";
import { formatJoinRequestDateTime, hasPendingJoinRequest } from "../../utils/joinRequest.helper";
import NotificationPopup from "./NotificationPopup";
import "./Project/projectCard.scss";
import ProjectAllocationChart from "./ProjectAllocationChart";
import { projectCardIcons } from "./project.constants";

/**
 * Reusable ProjectCard component that can be used in both popup and grid layouts
 * @param {Object} projectData - The project data to display
 * @param {boolean} isPopupMode - Whether this is being used in a popup or grid
 * @param {function} onClose - Close callback for popup mode
 * @param {Object} notificationSettings - Notification settings for the project
 * @param {function} setNotificationSettings - Function to update notification settings
 * @param {function} onEngagementEdit - Callback when engagement edit is clicked
 * @param {Object} notificationState - Notification popup state { notificationPopup, setNotificationPopup }
 * @author Sejal
 */
const ProjectCard = ({
  projectData,
  isPopupMode = false,
  onClose,
  notificationSettings = {},
  setNotificationSettings,
  onEngagementEdit,
  notificationState = {},
  uId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { selectedContentManagerItem } = useSelector((state) => state.contentManager);
  const bookmarks = useSelector((state) => state.bookmarks);
  const pathname = location.pathname;
  const {
    Allocation,
    WorkCategoryName,
    ProjectName,
    ProjectCode,
    members,
    QueueName,
    CostCenter,
    taskCount,
    RoleId,
    BusinessUnit,
    ProjectAllocation,
    IsBusinessUnitEnabled,
    IsCostCenterEnabled,
    ProjectId,
    AssignedTasks,
    OverDueTasks,
    RequestedOn,
    projectVariables
  } = projectData;

  const { notificationPopup, setNotificationPopup } = notificationState;

  const bellRef = useRef();

  // Expand/collapse state synced globally
  const [expanded, setExpanded] = useState(() => (typeof window !== "undefined" && window.__projectCardsExpanded) || false);
  const iconsScrollRef = useRef(null);
  const [iconScrollUi, setIconScrollUi] = useState({
    canScroll: false,
    showLeft: false,
    showRight: false,
  });

  /** Check if project is bookmarked*/
  const isProjectBookmarked = bookmarks?.projectBookmarks?.find((projectElement) => projectElement?.Id === ProjectId);

  // Listen for global expand/collapse toggle
  useEffect(() => {
    function handleGlobalToggle(e) {
      const { expanded: isExpanded } = e?.detail || {};
      setExpanded(!!isExpanded);
    }
    window.addEventListener("projectCardToggle", handleGlobalToggle);
    return () => window.removeEventListener("projectCardToggle", handleGlobalToggle);
  }, []);

  /**
   * Handles toggle expand/collapse for all project cards
   */
  const handleToggleExpandAll = useCallback(
    (e) => {
      e?.stopPropagation?.();
      const next = !expanded;
      setExpanded(next);
      if (typeof window !== "undefined") {
        window.__projectCardsExpanded = next;
      }
      window.dispatchEvent(new CustomEvent("projectCardToggle", { detail: { expanded: next } }));
    },
    [expanded]
  );

  /**
   * Handles closing the notification popup when clicking outside
   * @author Bhavana
   */
  useEffect(() => {
    if (!notificationPopup.show || notificationPopup.projectId !== ProjectId) return;
    function handleClickOutside(event) {
      const isNotificationPopup = event.target.closest(".notification-popup-content");
      const bellEl = bellRef.current;
      if (isNotificationPopup || (bellEl && bellEl.contains(event.target))) {
        return;
      }
      setNotificationPopup({ show: false, anchor: null, projectId: null });
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [notificationPopup?.show, notificationPopup?.projectId, ProjectId, setNotificationPopup]);

  /**
   * Changes the task view for a project and updates the user's last selected board
   */
  const changeTaskView = async (project) => {
    handleOpenTasksClick(project);
    if (isPopupMode && onClose) onClose();
  };

  /**
   * Changes the view to library view for a project
   */
  const changeToLibraryView = async (project) => {
    if (pathname.includes(label.LIBRARY_ROUTE) && selectedContentManagerItem?.projectId == project?.ProjectId) return;
    history.push(`${route.PRIVATE_ROUTE.CMS.PATH}/${project?.ProjectId}/${project.ProjectName}`);
    dispatch(setSelectedContentItem([]));
    dispatch(setFileMetaData({}));
    dispatch(setContentManagerDataView(label.CMS_GRID));
    dispatch(setContentManagerDataDetails([]));
    if (isPopupMode && onClose) onClose();
  };

  /**
   * Handles view changes for different project actions 
   */
  const handleViewChange = (value, project, iconLabel = null) => {
    if (uId) return;
    if (value == label.EMAILS) {
      history.push(route.PRIVATE_ROUTE.EMAILS.PATH + "/" + project?.ProjectId + "/" + encodeURIComponent(project?.ProjectName));
      if (isPopupMode && onClose) onClose();
      return;
    } else if (value == label.SETTINGS) {
      if (iconLabel === label.OVERVIEW) {
        history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project?.ProjectId}/overview`);
      }
       else if (iconLabel  === label.VIEW) {
        history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project?.ProjectId}/filters`);
      }
       else if (iconLabel === label.WORKFLOW) {
        history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project?.ProjectId}/workflows`);
      }
       else {
        history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project?.ProjectId}`);
      }
      if (isPopupMode && onClose) onClose();
    } else if (value) changeTaskView(project);
    else changeToLibraryView(project);
  };

  const handleProjectSettingsClick = (project) => {
    history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${project?.ProjectId}/overview`);
  };
  /**
   * Handles notification bell click for a project
   */
  const handleNotificationClick = (e, projectId) => {
    if (uId) return;
    if (e) e.preventDefault();
    setNotificationPopup((prev) => {
      if (prev.show && prev.projectId === projectId) {
        return { show: false, anchor: null, projectId: null };
      }
      return {
        show: true,
        anchor: bellRef,
        projectId,
      };
    });
  };

  /**
   * Handles push notification toggle for a project
   */
  const handlePushChange = (e, projectId, checked) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        push: checked,
      },
    }));
    const newValue = checked ? number.ONE.toString() : number.ZERO.toString();
    changeUserNotifEmailControl({
      userId: user?.id,
      projectId: projectId,
      isPushNotifEnabled: newValue,
      isEmailEnabled: null,
    });
  };

  /**
   * Handles email notification toggle for a project
   */
  const handleEmailChange = (e, projectId, checked) => {
    setNotificationSettings((prev) => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        email: checked,
      },
    }));
    const newValue = checked ? number.ONE.toString() : number.ZERO.toString();
    changeUserNotifEmailControl({
      userId: user?.id,
      projectId: projectId,
      isPushNotifEnabled: null,
      isEmailEnabled: newValue,
    });
  };

/**
 * Updates the icon scroll arrows based on the scroll position
 */
  const updateIconScrollArrows = useCallback(() => {
    const el = iconsScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const canScroll = maxScroll > number.TWO;
    setIconScrollUi({
      canScroll,
      showLeft: canScroll && scrollLeft > number.TWO,
      showRight: canScroll && scrollLeft < maxScroll - number.TWO,
    });
  }, []);

  /**
   * Updates the icon scroll arrows based on the scroll position
   */
  useEffect(() => {
    const el = iconsScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      updateIconScrollArrows();
      return undefined;
    }
    const ro = new ResizeObserver(() => updateIconScrollArrows());
    ro.observe(el);
    const inner = el.firstElementChild;
    if (inner) ro.observe(inner);
    updateIconScrollArrows();
    return () => ro.disconnect();
  }, [updateIconScrollArrows, ProjectId]);

  /**
   * Scrolls the project icons in the given direction
   */
  const scrollProjectIcons = useCallback(
    (direction, e) => {
      if (e) e.stopPropagation();
      const el = iconsScrollRef.current;
      if (!el) return;
      const delta = Math.max(80, el.clientWidth * 0.75) * direction;
      el.scrollBy({ left: delta, behavior: "smooth" });
    },
    []
  );

  const handleProjectCardIconClick = (item) => {
    if (uId) return; handleViewChange(item.text, projectData, item.label);
    
  };

  const renderProjectIconScrollArrow = (next) => {
    const show = next ? iconScrollUi.showRight : iconScrollUi.showLeft;
    return (
      <div className='d-flex align-items-center justify-content-center flex-shrink-0 '>
        <div
          className='project-icon-nav-arrow cursor-pointer d-flex align-items-center justify-content-center flex-shrink-0'
          onClick={(e) => show && scrollProjectIcons(next ? 1 : -1, e)}
          title={next ? label.SHOW_MORE : button.BACK}
          style={{
            visibility: show ? "visible" : "hidden",
            pointerEvents: show ? "auto" : "none",
          }}>
          <span className='project-icon'>{next ? icon.RIGHT : icon.LEFT}</span>
        </div>
      </div>
    );
  };

  /**
   * Handles opening the tasks view for a project
   */
  const handleOpenTasksClick = (project) => {
    if (uId) return;
    dispatch(updateCurrUser({ lastSelectedBoard: label.GRID }));
    dispatch(setRedirectedFromSettings({ isRedirected: true, redirectedFrom: "projectTab" }));
    history.push(`${route.PRIVATE_ROUTE.TASKS.PATH}/${project?.ProjectId}/${encodeURIComponent(project.ProjectName)}`);
  };

  /**
   * Handles bookmarking/unbookmarking a project
   * @author Muskan Thakur
   */
  const handleFavoriteClick = async (e, projectId) => {
    if (uId) return;
    if (e) e.stopPropagation();

    if (isProjectBookmarked || bookmarks?.projectBookmarks?.length < number?.TEN) {
      const payload = {
        userId: user?.id,
        entityType: label.PROJECT,
        entityId: projectId,
        sortId: number?.ZERO,
      };
      await dispatch(updateBookmark(payload));
    } else {
      dispatch(alertNotification(true, createDynamicTeamName(quote?.PROJECT_BOOKMARK_ALERT, user?.operationalTeamName), notifyIcon?.ERROR_ICON));
    }
  };

  const handleMemberNav = (projectData) => {
    history.push(`${route.PRIVATE_ROUTE.TEAM_SETTING.PATH}/${projectData?.ProjectId}/members`);
  };

  const isPendingJoinRequest = hasPendingJoinRequest(projectData);

  return (
    <>
      <div
        className={`project-card border ${isPopupMode ? "popup-mode" : ""} ${expanded ? "expanded" : "collapsed"} ${
          isPendingJoinRequest ? "pending-join-request" : ""
        }`}>
        <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
          {isPendingJoinRequest ? (
            <div className='project-card-header'>
              <div className='project-info'>
                <div className='project-name-wrapper mb-2 position-relative'>
                  <div className='d-flex align-items-center w-100'>
                    <div className='project-name font-weight-bold text-truncate flex-grow-1'>{ProjectName}</div>
                  </div>
                </div>
                <div className='awaiting-response-section p-2 border rounded mb-2'>
                  <div className='d-flex align-items-center mb-1'>
                    <span className='awaiting-icon mr-2'>{icon.CLOCK}</span>
                    <span className='awaiting-text'>{label.AWAITING_RESPONSE}</span>
                  </div>
                  <div className='d-flex align-items-center'>
                    <span className='clock-icon mr-2'>{icon.CLOCK}</span>
                    <span className='request-date text-muted small'>{formatJoinRequestDateTime(RequestedOn)}</span>
                  </div>
                </div>
                <button
                  className='btn btn-outline-secondary btn-sm cancel-request-btn w-100'
                  onClick={() => {
                    getNotification(label.REQUEST_CANCELLED, notifyIcon.SUCCESS_ICON);
                  }}
                  title={label.CANCEL_REQUEST}>
                  <span className='mr-1'>{icon.ARROW_LEFT}</span>
                  {label.CANCEL_REQUEST}
                </button>
              </div>
            </div>
          ) : (
            <div className='project-card-header'>
              <div className='project-info'>
                <div className='project-name-wrapper mb-2 position-relative'>
                  <div className='d-flex align-items-center w-100'>
                    <span
                      className='project-favorite-star cursor-pointer mr-2 d-flex'
                      onClick={(e) => handleFavoriteClick(e, ProjectId)}
                      title={isProjectBookmarked ? label.REMOVE_FAVORITE : tooltip.ADD_TO_FAVORITES}>
                      {isProjectBookmarked ? <span className='without-mark-star'>{icon.FAVORITE}</span> : <span>{icon.MARK_FAVORITE}</span>}
                    </span>
                    <div
                      onClick={() => handleProjectSettingsClick(projectData)}
                      className='project-name font-weight-bold text-truncate cursor-pointer flex-grow-1'>
                      {ProjectName}
                    </div>
                  </div>
                  <div
                    className='project-hover-actions position-absolute top-0 right-0 d-flex align-items-center flex-nowrap gap-2 w-auto overflow-visible opacity-0 invisible pointer-events-none border-0 rounded'>
                    <span className='project-code bg-white pl-2'>{ProjectCode}</span>
                    <span
                      id={`notification-bell-${ProjectId}`}
                      className={`${
                        notificationSettings[ProjectId]?.push === 1 || notificationSettings[ProjectId]?.email === 1 ? "blue active-notification" : ""
                      } cursor-pointer notification-bell d-flex`}
                      ref={bellRef}
                      onClick={(e) => handleNotificationClick(e, ProjectId)}>
                      {icon?.VOLUME2}
                    </span>
                  </div>
                </div>
                <div className='icons-container-wrapper position-relative w-100 overflow-hidden'>
                  <div className='d-flex align-items-center w-100 flex-nowrap'>
                    {iconScrollUi?.canScroll && renderProjectIconScrollArrow(false)}

                    <div
                      ref={iconsScrollRef}
                      className='project-card-icons-scroll flex-grow-1'
                      onScroll={updateIconScrollArrows}>
                      <div
                        className={`project-card-icons-row d-flex align-items-center flex-nowrap ${
                          iconScrollUi.canScroll ? "project-card-icons-row--overflow" : "w-100 justify-content-between"
                        }`}>
                        {projectCardIcons.map((item, idx) => (
                          <div
                            key={item.label}
                            id={`project-card-icon-${idx}-${ProjectId}`}
                            className='project-icon-item cursor-pointer d-flex flex-column align-items-center'
                            onClick={() => handleProjectCardIconClick(item)}>
                            <span>
                              <span className='project-icon'>{item.icon}</span>
                              <span className='project-text'>{item.label}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {iconScrollUi.canScroll && renderProjectIconScrollArrow(true)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isPendingJoinRequest && (
            <>
              <div className='mb-3'>
                <div className='project-engagement-section d-flex justify-content-between'>
                  <span className='w-75 hover-blue-colour cursor-pointer' onClick={() => onEngagementEdit && onEngagementEdit(projectData)}>{label.ENGAGEMENT}</span>
                  <span className='user-role'>{RoleId === number.FOUR ? label.CREATOR : ""}</span>
                </div>
                <div className='d-flex mb-3 mt-3 justify-content-between align-items-center'>
                  <div className='d-flex justify-content-center align-items-center project-allocation-section engagement-container'>
                    {ProjectAllocation && ProjectAllocation > 0 ? (
                      <ProjectAllocationChart
                        allocation={Allocation}
                        workCategoryName={WorkCategoryName}
                        projectAllocation={ProjectAllocation}
                        onEngagementEdit={onEngagementEdit}
                        projectData={projectData}
                      />
                    ) : (
                      <div className='no-engagement-container d-flex justify-content-center align-items-center'>
                        <div className='no-engagement-default d-flex justify-content-center align-items-center'>
                          <CircleOff className='no-engagement-off-icon' />
                        </div>
                        <div className='no-engagement-hover d-flex flex-column justify-content-center align-items-center'>
                          <span
                            className='no-engagement-edit-icon square-icon square-icon-extra-small'
                            onClick={() => onEngagementEdit && onEngagementEdit(projectData)}>
                            {icon.EDIT_ICON}
                          </span>
                          <span className='no-engagement-text cursor-pointer' onClick={() => onEngagementEdit && onEngagementEdit(projectData)}>
                            {label.NIL}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center justify-content-center flex-wrap">
                  <div className='d-flex flex-column justify-content-start'>
                    {RoleId != null && (AssignedTasks > 0 || OverDueTasks > 0) ? (
                      <>
                        {AssignedTasks > 0 && (
                          <span className='task-item d-flex flex-row cursor-pointer' onClick={() => changeTaskView(projectData)}>
                            <span className='hover-blue-colour'>
                              {AssignedTasks} {label.ASSIGNED_TASKS}
                            </span>
                          </span>
                        )}
                        {OverDueTasks > 0 && (
                          <div className='task-item d-flex flex-row cursor-pointer mt-1' onClick={() => changeTaskView(projectData)}>
                            <span className='hover-blue-colour'>
                              {OverDueTasks} {label.OVERDUE_TASKS}
                            </span>
                          </div>
                        )}
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                  <div
                    className='cursor-pointer icon-small text-muted ml-4 project-card-toggle'
                    onClick={handleToggleExpandAll}
                    title={expanded ? label.COLLAPSE_CARDS : label.EXPAND_CARDS}>
                    {expanded ? icon.CHEVRON_UP : icon.CHEVRON_DOWN}
                  </div>
                  </div>
                </div>
              </div>
              <div className={`project-card-body ${expanded ? "expanded" : "collapsed"}`}>
                <ul className='project-details'>
                  <li>
                    <span className='project-details-icon'>{icon.PERSON_TOGGLE}</span>
                    <span
                      className='cursor-pointer hover-blue-colour'
                      onClick={() => {
                        handleMemberNav(projectData);
                      }}>{`${members} Members`}</span>
                    {RoleId != null && !!(taskCount > 0) && (
                      <span className='ml-auto'>
                        <span className='project-details-icon'>{icon.ADD_TASK_APP}</span>
                        <span
                          id='project-card-task-count'
                          className={`${uId ? "" : "cursor-pointer hover-blue-colour"}`}
                          onClick={() => handleOpenTasksClick(projectData)}>{`${taskCount} Open Tasks`}</span>
                      </span>
                    )}
                  </li>
                 {projectVariables?.map((variable) => (
                  <li key={variable?._id ?? variable?.name} className="project-details-variable-item">
                    <div className="project-details-variable-inner">
                      <span className="text-truncate project-details-variable-label">{variable?.name}</span>
                      <span className="project-details-variable-sep" aria-hidden="true">
                        -
                      </span>
                      <span className="text-truncate project-details-variable-value">
                        {variable?.options?.find((option) => option.value === variable.value)?.label ?? ""}
                      </span>
                    </div>
                  </li>
                 ))} 

                </ul>
              </div>
            </>
          )}
        </Tooltip>
      </div>

      {notificationPopup.show && notificationPopup.projectId === ProjectId && (
        <NotificationPopup
          anchor={bellRef?.current}
          show={notificationPopup.show}
          onClose={() => setNotificationPopup({ show: false, anchor: null, projectId: null })}
          pushEnabled={notificationSettings[ProjectId]?.push || false}
          emailEnabled={notificationSettings[ProjectId]?.email || false}
          onPushChange={(e) => handlePushChange(e, ProjectId, e.value)}
          onEmailChange={(e) => handleEmailChange(e, ProjectId, e.value)}
        />
      )}
    </>
  );
};

export default ProjectCard;
